/*
  Adds dots and autoplay to Dawn's own <slider-component>.
  Dawn's slider-component (global.js) still handles swipe, arrows and the counter;
  this element only reads its pages and scrolls the same slider.
*/
if (!customElements.get('limbio-product-slider')) {
  class LimbioProductSlider extends HTMLElement {
    connectedCallback() {
      this.sliderComponent = this.querySelector('slider-component');
      this.dotsWrapper = this.querySelector('[data-slider-dots]');
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = Math.max(parseInt(this.dataset.speed || '5', 10), 2) * 1000;
      this.paused = false;

      if (!this.sliderComponent) return;

      customElements.whenDefined('slider-component').then(() => {
        // Wait one frame so Dawn's slider has measured its pages
        requestAnimationFrame(() => this.init());
      });
    }

    disconnectedCallback() {
      this.stop();
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    init() {
      this.slider = this.sliderComponent.slider || this.sliderComponent.querySelector('[id^="Slider-"]');
      if (!this.slider) return;

      this.buildDots();
      this.slider.addEventListener('scroll', () => this.updateDots(), { passive: true });

      this.resizeObserver = new ResizeObserver(() => {
        this.buildDots();
      });
      this.resizeObserver.observe(this.slider);

      if (this.autoplay && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        ['mouseenter', 'focusin', 'touchstart'].forEach((event) =>
          this.addEventListener(event, () => (this.paused = true), { passive: true })
        );
        ['mouseleave', 'focusout'].forEach((event) => this.addEventListener(event, () => (this.paused = false)));
        this.addEventListener('touchend', () => setTimeout(() => (this.paused = false), 3000), { passive: true });
        this.start();
      }
    }

    // Uses the values Dawn's slider-component already calculated
    getPages() {
      const sc = this.sliderComponent;
      const items = Array.from(this.slider.querySelectorAll('[id^="Slide-"]')).filter((el) => el.clientWidth > 0);
      if (items.length < 2) return { total: 1, offset: 0, current: 1 };
      const offset = items[1].offsetLeft - items[0].offsetLeft;
      const perPage = Math.max(Math.floor((this.slider.clientWidth - items[0].offsetLeft) / offset), 1);
      const total = Math.max(items.length - perPage + 1, 1);
      const isSlider = this.slider.scrollWidth > this.slider.clientWidth + 2;
      const current = Math.round(this.slider.scrollLeft / offset) + 1;
      return {
        total: isSlider ? (sc.totalPages || total) : 1,
        offset: sc.sliderItemOffset || offset,
        current: Math.min(current, total),
      };
    }

    buildDots() {
      if (!this.dotsWrapper) return;
      const { total } = this.getPages();
      if (this.dotsCount === total) {
        this.updateDots();
        return;
      }
      this.dotsCount = total;
      this.dotsWrapper.innerHTML = '';
      this.dotsWrapper.hidden = total < 2;

      const label = this.dotsWrapper.dataset.label || 'Slide';
      for (let i = 1; i <= total; i += 1) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'slider-counter__link slider-counter__link--dots link';
        button.setAttribute('aria-label', `${label} ${i}`);
        button.innerHTML = '<span class="dot"></span>';
        button.addEventListener('click', () => this.goTo(i));
        this.dotsWrapper.appendChild(button);
      }
      this.updateDots();
    }

    updateDots() {
      if (!this.dotsWrapper) return;
      const { current } = this.getPages();
      this.dotsWrapper.querySelectorAll('.slider-counter__link').forEach((dot, index) => {
        const active = index + 1 === current;
        dot.classList.toggle('slider-counter__link--active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    goTo(page) {
      const { offset } = this.getPages();
      this.slider.scrollTo({ left: (page - 1) * offset, behavior: 'smooth' });
    }

    start() {
      this.stop();
      this.timer = setInterval(() => {
        if (this.paused || document.hidden) return;
        const { total, current } = this.getPages();
        if (total < 2) return;
        this.goTo(current >= total ? 1 : current + 1);
      }, this.speed);
    }

    stop() {
      clearInterval(this.timer);
    }
  }

  customElements.define('limbio-product-slider', LimbioProductSlider);
}